import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSportIcon } from '../utils/sportEmoji';
import { Activity } from '../types/activity';
import { useScrollDismiss } from '../hooks/useScrollDismiss';
import '../styles/SportBreakdown.css';

import { BarSegment, buildSportSegments } from '../utils/sportData';

interface Props {
  activities: Activity[];
  currentDayIndex: number;
  visible: boolean;
  title?: string;
}

export default function SportBreakdownBar({ activities, currentDayIndex, visible, title }: Props) {
  const [activeSeg, setActiveSeg] = useState<BarSegment | null>(null);
  const [tipStyle, setTipStyle] = useState<React.CSSProperties>({});
  const segRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);

  useScrollDismiss(activeSeg, setActiveSeg);

  // close on outside click (touch)
  useEffect(() => {
    if (!activeSeg || !isTouch.current) return;
    const handler = () => setActiveSeg(null);
    document.addEventListener('click', handler, { once: true });
    return () => document.removeEventListener('click', handler);
  }, [activeSeg]);

  // Nur Aktivitäten bis zum aktuellen Tag (inklusive)
  const filteredActs = useMemo(() => {
    return activities.filter(a => {
      const d = new Date(a.started_at_utc.replace(' ', 'T') + 'Z');
      const dayOfWeek = (d.getUTCDay() + 6) % 7; // Mo=0, So=6
      return dayOfWeek <= currentDayIndex;
    });
  }, [activities, currentDayIndex]);

  const segs = useMemo(() => {
    if (filteredActs.length === 0) return [];
    let total = 0;
    for (const a of filteredActs) {
      total += Number(a.base_points) || 0;
      total += Number(a.challenge_bonus_points) || 0;
      total += Number(a.pr_bonus_points) || 0;
      total += Number((a as any).trophy_boost_amount) || 0;
    }
    return buildSportSegments(filteredActs, Math.max(total, 1));
  }, [filteredActs]);
  const totalPts = filteredActs.reduce((s, a) => s + (Number(a.total_points) || 0), 0);

  const computeStyle = useCallback((el: HTMLDivElement): React.CSSProperties => {
    const r     = el.getBoundingClientRect();
    const tipW  = 220;
    let left    = Math.round(r.left + r.width / 2 - tipW / 2);
    if (left + tipW > window.innerWidth - 8) left = window.innerWidth - tipW - 8;
    if (left < 8) left = 8;
    const spaceAbove = r.top;
    if (spaceAbove >= 80) {
      return { position: 'fixed', bottom: Math.round(window.innerHeight - r.top + 6), left, width: tipW };
    }
    return { position: 'fixed', top: Math.round(r.bottom + 6), left, width: tipW };
  }, []);

  const openTip  = useCallback((seg: BarSegment, el: HTMLDivElement) => {
    setTipStyle(computeStyle(el));
    setActiveSeg(seg);
  }, [computeStyle]);
  const closeTip = useCallback(() => setActiveSeg(null), []);

  const handleEnter = (seg: BarSegment) => {
    if (isTouch.current) return;
    const el = segRefs.current.get(seg.key);
    if (el) openTip(seg, el);
  };
  const handleLeave = () => { if (!isTouch.current) closeTip(); };
  const handleClick = (seg: BarSegment, e: React.MouseEvent) => {
    if (!isTouch.current) return;
    e.stopPropagation();
    if (activeSeg?.key === seg.key) { closeTip(); return; }
    const el = segRefs.current.get(seg.key);
    if (el) openTip(seg, el);
  };

  if (segs.length === 0) return null;

  let cumPct = 0;

  const tooltip = activeSeg
    ? ReactDOM.createPortal(
        <div className="chip-tooltip sdb-tooltip" style={tipStyle}>
          <div className="tip-title" style={{ color: activeSeg.color }}>
            <FontAwesomeIcon icon={getSportIcon(activeSeg.key)} style={{ marginRight: 6 }} />
            {activeSeg.label}
          </div>
          <div className="tip-row tip-meta">{activeSeg.title}</div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={`sport-breakdown-bar${visible ? ' sport-breakdown-bar--visible' : ''}`}>
      <div className="sdb-header">
        <span className="sdb-title">{title || 'Points by Sport'}</span>
        <span className="sdb-total">{Math.round(totalPts)}P</span>
      </div>
      <div className="sdb-bar-track">
        {segs.map((seg, i) => {
          const pct = seg.widthPct;
          const left = cumPct;
          cumPct += pct;
          return (
            <div
              key={seg.key}
              ref={el => { if (el) segRefs.current.set(seg.key, el); }}
              className="sdb-bar-seg"
              style={{
                width: visible ? `${pct}%` : '0%',
                marginLeft: i === 0 ? 0 : undefined,
                backgroundColor: seg.color,
                transitionDelay: visible ? `${i * 80}ms` : '0ms',
                transitionDuration: '500ms',
              }}
              onMouseEnter={() => handleEnter(seg)}
              onMouseLeave={handleLeave}
              onClick={(e) => handleClick(seg, e)}
            >
              {pct >= 5 && (
                <span className="sdb-bar-seg-label">
                  <span className="sdb-bar-seg-label-top">
                    <FontAwesomeIcon icon={getSportIcon(seg.key)} />
                    <span className="sdb-bar-seg-label-pct">{Math.round(pct)}%</span>
                  </span>
                  <span className="sdb-bar-seg-label-name">{seg.label}</span>
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="sdb-legend">
        {segs.filter(seg => seg.key !== 'Betting').map(seg => (
          <div key={seg.key} className="sdb-legend-item">
            <span className="sdb-legend-dot" style={{ background: seg.color }} />
            <FontAwesomeIcon icon={getSportIcon(seg.key)} className="sdb-legend-icon" />
            <span className="sdb-legend-label">{seg.label}</span>
            <span className="sdb-legend-pts">{Math.round(seg.widthPct)}%</span>
          </div>
        ))}
      </div>
      {tooltip}
    </div>
  );
}
